import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { type Address, erc4626Abi } from 'viem';
import { useReadContract } from 'wagmi';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useChangeAfterFetching } from '@/hooks/useChangeAfterFetching';
import { useLatestBlock } from '@/hooks/useLatestBlock';

/**
 * Hook that fetches user's vault position.
 * The read is pinned to the shared latest-block snapshot so it resolves at the
 * same block as the vault data in `useVaultV2`.
 * @param params - Parameters object
 * @param params.vaultAddress - Vault contract address
 * @param params.chainId - The vault's chain
 * @returns Object with user shares, a placeholder flag transaction builders must
 *   gate on, loading state, error, and a refetch that advances the pinned block
 */
export function useVaultV2Position(t0) {
  const $ = _c(29);
  const {
    vaultAddress,
    chainId
  } = t0;
  const {
    account
  } = useWeb3Context();
  let t1;
  if ($[0] !== account.address) {
    t1 = isDefined(account.address);
    $[0] = account.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      enabled: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: block,
    isLoading: isBlockLoading,
    isFetching: isFetchingBlock,
    error: blockError,
    refetch: refetchBlock
  } = useLatestBlock(chainId, t2);
  const blockNumber = block?.number;
  let t3;
  if ($[4] !== account.address) {
    t3 = [account.address];
    $[4] = account.address;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== account.address || $[7] !== blockNumber || $[8] !== chainId) {
    t4 = isDefined(account.address) && isDefined(chainId) && isDefined(blockNumber);
    $[6] = account.address;
    $[7] = blockNumber;
    $[8] = chainId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== account.address || $[11] !== chainId || $[12] !== vaultAddress) {
    t5 = (previousData, previousQuery) => {
      const previous = previousQuery?.queryKey[1] as {
        address?: Address;
        chainId?: number;
        args?: readonly unknown[];
      } | undefined;
      if (!previous) {
        return;
      }
      const samePosition = previous.address === vaultAddress && previous.chainId === chainId && previous.args?.[0] === account.address;
      return samePosition ? previousData : undefined;
    };
    $[10] = account.address;
    $[11] = chainId;
    $[12] = vaultAddress;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = {
      enabled: t4,
      placeholderData: t5,
      staleTime: Infinity,
      gcTime: 20000
    };
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== blockNumber || $[18] !== chainId || $[19] !== t3 || $[20] !== t6 || $[21] !== vaultAddress) {
    t7 = {
      address: vaultAddress,
      abi: erc4626Abi,
      functionName: "balanceOf",
      args: t3,
      chainId,
      blockNumber,
      query: t6
    };
    $[17] = blockNumber;
    $[18] = chainId;
    $[19] = t3;
    $[20] = t6;
    $[21] = vaultAddress;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const {
    data: userVaultShares,
    isLoading: isUserVaultSharesLoading,
    isFetching: isFetchingUserVaultShares,
    isPlaceholderData: isUserVaultSharesPlaceholder,
    error: userVaultSharesError
  } = useReadContract(t7);
  const memoizedUserVaultSharesError = useChangeAfterFetching(userVaultSharesError, isFetchingUserVaultShares);
  const memoizedBlockError = useChangeAfterFetching(blockError, isFetchingBlock);
  const t8 = isUserVaultSharesLoading || isBlockLoading;
  const t9 = memoizedUserVaultSharesError || memoizedBlockError;
  let t10;
  if ($[23] !== isUserVaultSharesPlaceholder || $[24] !== refetchBlock || $[25] !== t8 || $[26] !== t9 || $[27] !== userVaultShares) {
    t10 = {
      userVaultShares,
      isUserVaultSharesPlaceholder,
      isLoading: t8,
      error: t9,
      refetch: refetchBlock
    };
    $[23] = isUserVaultSharesPlaceholder;
    $[24] = refetchBlock;
    $[25] = t8;
    $[26] = t9;
    $[27] = userVaultShares;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}